(ns practitest.frontend.components2.dashboard.charts.axis
  (:require ["d3" :as d3]
            [practitest.frontend.components2.dashboard.charts.common :as c]
            [practitest.frontend.common.format-utils :as format]
            [tick.core :as t]))

(defn- rotate-ticks! [sel {:keys [label-angle]}]
  (when label-angle
    (-> (.selectAll sel ".tick text")
        (.attr "transform" (str "rotate(" label-angle ")"))
        (.attr "dx" (if (neg? label-angle) "-0.5em" "0.5em"))
        (.attr "dy" "0.5em")
        (.style "text-anchor" (if (neg? label-angle) "end" "start")))))

(defn- show-every-nth-label! [sel n]
  (-> (.selectAll sel ".tick text")
      (.style "display" (fn [_ i]
                          (if (zero? (mod i n)) "block" "none")))))

(defn- d3-format [type fmt]
  (case type
    :temporal     (.utcFormat d3 fmt)
    :quantitative (.format d3 fmt)
    nil))

(defn- exact-tick-values [scale n]
  (let [[d0 d1] (.domain scale)
        t0      (.valueOf d0)
        t1      (.valueOf d1)
        step    (/ (- t1 t0) (dec n))]
    (clj->js (mapv #(+ t0 (* % step)) (range n)))))

(defn- cross-len [orient width height]
  (case orient
    (:top :bottom) height
    (:left :right) width))

(defn- orient->d3-axis [orient d3-scale]
  (case orient
    :top    (.axisTop d3 d3-scale)
    :bottom (.axisBottom d3 d3-scale)
    :left   (.axisLeft d3 d3-scale)
    :right  (.axisRight d3 d3-scale)))

(defn- min-step-tick-values [scale tick-min-step]
  (let [[d0 d1] (.domain scale)
        start   (* tick-min-step (js/Math.ceil (/ d0 tick-min-step)))
        end     (* tick-min-step (js/Math.floor (/ d1 tick-min-step)))
        n-steps (if (<= start end) (/ (- end start) tick-min-step) -1)]
    (if (< n-steps 8)
      (clj->js (range start (+ end tick-min-step) tick-min-step))
      (->> (.ticks scale 8)
           js->clj
           (map #(-> (/ % tick-min-step)
                     js/Math.round
                     (* tick-min-step)))
           distinct
           sort
           clj->js))))

(defn- d3-axis [{:keys [type d3-scale axis]} width height]
  (let [{:keys [orient format tick-count ticks grid tick-min-step]} axis
        tick-size      (- (cross-len orient width height))
        q?             (= type :quantitative)
        has-min-step?  (contains? axis :tick-min-step)
        valid-step?    (and has-min-step? (number? tick-min-step) (pos? tick-min-step))
        integer-step?  (and valid-step? (== tick-min-step (js/Math.floor tick-min-step)))
        fmt            (or format (when (and q? integer-step?) ".0f"))]
    (cond-> (orient->d3-axis orient d3-scale)
      fmt                            (.tickFormat (d3-format type fmt))
      (and q? valid-step?
           (not (false? ticks)))     (.tickValues (min-step-tick-values d3-scale tick-min-step))
      (and (not has-min-step?) tick-count)
                                     (.tickValues (exact-tick-values d3-scale tick-count))
      grid                           (-> (.tickSize tick-size) (.tickSizeOuter 0))
      (false? ticks)                 (.tickValues #js []))))

(defn- title-transform [orient width height]
  (case orient
    :top    (str "translate(" (/ width 2) ",-25)")
    :bottom (str "translate(" (/ width 2) ",35)")
    :left   (str "translate(-40," (/ height 2) ") rotate(-90)")
    :right  (str "translate(40," (/ height 2) ") rotate(90)")))

(defn- ensure-title! [sel {:keys [orient title]} width height]
  (when title
    (-> (c/ensure-el! sel "text" "axis-title")
        (.attr "text-anchor" "middle")
        (.attr "transform" (title-transform orient width height))
        (.text title))))

(defn- render-axis! [sel {:keys [axis] :as channel} width height]
  (when channel
    (let [d3-ax     (d3-axis channel width height)
          sel       (.call sel d3-ax)
          label-stride (:label-stride axis)]
      (when label-stride
        (show-every-nth-label! sel label-stride))
      (rotate-ticks! sel axis)
      (ensure-title! sel axis width height))))

(defn render! [svg {{:keys [x y]} :encode
                    :keys         [width height]}]
  (let [x-g (.select svg "g.chart g.x-axis")
        y-g (.select svg "g.chart g.y-axis")]
    (render-axis! x-g x width height)
    (render-axis! y-g y width height)))

